<template>
	<ul role="list" class="rounded-lg border divide-y divide-gray-100 bg-white">
		<li class="heading">
			<div class="text-center flex-none w-16">
				<label>{{ localization.t("administration.organizations.columns.active") }}</label>
			</div>
			<div class="min-w-0 flex-auto">
				<label>{{ localization.t("administration.organizations.columns.name") }}</label>
			</div>
			<template v-if="!selectedOrganizationId">
				<div class="flex-none w-[160px]">
					<label>{{ localization.t("administration.organizations.columns.plan") }}</label>
				</div>
				<div class="flex-none w-[160px]">
					<label>{{ localization.t("administration.organizations.columns.max-companies") }}</label>
				</div>
				<div class="flex-none w-[140px]">
					<label>{{ localization.t("administration.organizations.columns.total-users") }}</label>
				</div>
				<div class="flex-none w-[140px]">
					<label>{{ localization.t("administration.organizations.columns.created-on") }}</label>
				</div>
			</template>
			<div class="flex-none w-[70px]"></div>
			<div class="flex-none w-14">&nbsp;</div>
		</li>
		<li v-for="item in items" :key="item.id" class="flex items-center justify-between gap-x-4 px-3 py-0"
			:class="selectedOrganizationId === item.id && 'bg-gray-100'">
			<div class="flex-none py-3 w-16 text-center">
				<CheckCircleIcon v-if="item.active" class="h-10 w-10 text-green-600" />
				<XCircleIcon v-else class="h-10 w-10 text-gray-500" />
			</div>
			<div class="min-w-0 flex-auto">
				<label class="text-2xl font-bold text-gray-900">{{ item.name }}</label>
			</div>
			<template v-if="!selectedOrganizationId">
				<div class="flex-none w-[160px]">
					<label>{{ item.planName }}</label>
				</div>
				<div class="flex-none w-[160px]">
					<label>{{ item.maxCompanies }}</label>
				</div>
				<div class="flex-none w-[140px]">
					<label>{{ item.totalUsers ?? 0 }}</label>
				</div>
				<div class="flex-none w-[140px]">
					<label>{{ formatApiDate(item.createdOn) }}</label>
				</div>
			</template>
			<div class="flex-none w-[70px]">
				<button v-if="!selectedOrganizationId || selectedOrganizationId === item.id" class="btn btn-xs flex items-end"
					@click="emit('onEditClick', item.id)">
					<span class="w-7 h-7 text-center"><pencil-alt-icon class="w-6 h-7" /></span>
					Edit
				</button>
			</div>
			<div class="flex-none py-3 w-14 cursor-pointer" @click="emit('onDetailsClick', item.id)">
				<p>
					<ChevronRightIcon class="h-8 w-8 text-gray-600" />
				</p>
			</div>
		</li>
	</ul>
	<ul class="no-data bg-white" v-if="!items || items.length === 0">
		<li>{{ localization.t("administration.organizations.no-data") }}</li>
	</ul>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { CheckCircleIcon, XCircleIcon, ChevronRightIcon, PencilAltIcon } from "@heroicons/vue/outline";
import IOrganization from "@/interfaces/organizations/iOrganization";
import { formatApiDate } from "@/utils/dateTime";

defineProps({
	items: {
		type: Array as () => IOrganization[],
		default: () => [],
	},
	selectedOrganizationId: String,
});

const emit = defineEmits(["onDetailsClick", "onEditClick"]);

const localization = useI18n();
</script>

<style scoped lang="postcss">
li.heading {
	position: sticky;
	top: 0;
	height: 48px;
	@apply flex leading-5 items-center bg-gray-100 text-gray-500 text-lg align-middle justify-between gap-x-4 px-3 py-3;
}

li svg {
	display: inline;
}

ul {
	max-height: calc(100% - 32px);
	overflow-y: auto;
}

ul.no-data li {
	text-align: center;
	font-size: 1.6rem;
	color: #999;
	border: none;
	padding: 5px;
}
</style>
